import { mockSubUsersData } from "../data/mockData/wlDemoData";

const STORAGE_EXTRAS = "wl_bs_subusers_extras";
const STORAGE_OVERRIDES = "wl_bs_subusers_overrides";

const emptyProfileImage = { thumb_small: "", thumb_medium: "" };

function readJson(key, fallback) {
  if (typeof sessionStorage === "undefined") {
    return fallback;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) {
      return fallback;
    }
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

function mergeById(baseRows, extraRows) {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extraRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), { ...(map.get(String(row.id)) || {}), ...row });
    }
  });
  return Array.from(map.values());
}

function getNextSubUserId(rows) {
  return (rows || []).reduce((max, row) => {
    const val = Number(row?.id) || 0;
    return val > max ? val : max;
  }, 200) + 1;
}

function normalizeBool(value, fallback = false) {
  if (value === true || value === "true" || value === "1" || value === 1) {
    return true;
  }
  if (value === false || value === "false" || value === "0" || value === 0) {
    return false;
  }
  return fallback;
}

function normalizeSubUserPayload(payload = {}) {
  const firstName = String(payload.first_name || "").trim();
  const lastName = String(payload.last_name || "").trim();
  const company = String(payload.company || "").trim();
  const friendlyNameRaw = String(payload.friendly_name || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  const friendlyName =
    friendlyNameRaw ||
    (company ? (fullName ? `${fullName} (${company})` : company) : fullName);

  return {
    first_name: firstName,
    last_name: lastName,
    email: String(payload.email || payload.username || "").trim(),
    username: String(payload.username || payload.email || "").trim(),
    friendly_name: friendlyName,
    company,
    contract_expiry: payload.contract_expiry || null,
    sub_user_deal: payload.sub_user_deal || "",
    national_tax_number: payload.national_tax_number || "",
    business_reg_number: payload.business_reg_number || "",
    street_and_number: payload.street_and_number || "",
    postal_code: payload.postal_code || "",
    city: payload.city || "",
    country: payload.country || "",
    phone: payload.phone || "",
    can_add_invoices: normalizeBool(payload.can_add_invoices, false),
    distribution: normalizeBool(payload.distribution, false),
    release_transfer: normalizeBool(payload.release_transfer, false),
    is_active: normalizeBool(payload.is_active, true),
    is_blacklisted: normalizeBool(payload.is_blacklisted, false),
    invoices: normalizeBool(payload.invoices, true),
    music_link: normalizeBool(payload.music_link, true),
    music_link_urlparts_allowance: Number(payload.music_link_urlparts_allowance) || 5,
    profile_image: payload.profile_image || emptyProfileImage,
  };
}

export function getSubUserRows() {
  const extras = readJson(STORAGE_EXTRAS, []);
  const overrides = readJson(STORAGE_OVERRIDES, {});
  const merged = mergeById(mockSubUsersData, extras);
  return merged.map(row => {
    const patch = overrides[String(row.id)];
    return patch ? { ...row, ...patch } : row;
  });
}

export function getSubUserById(id) {
  return getSubUserRows().find(row => String(row.id) === String(id)) || null;
}

export function validateSubUserEmail() {
  return { valid: true };
}

export function validateSubUserUsername() {
  return { valid: true };
}

export function handleCreateSubUser(payload) {
  const normalized = normalizeSubUserPayload(payload);
  if (!normalized.username) {
    throw new Error("Username is required.");
  }
  if (!normalized.email) {
    throw new Error("Email is required.");
  }

  const rows = getSubUserRows();
  const duplicate = rows.find(
    row =>
      String(row.username).toLowerCase() === normalized.username.toLowerCase() ||
      String(row.email).toLowerCase() === normalized.email.toLowerCase(),
  );
  if (duplicate) {
    throw new Error("A sub-user with this username or email already exists.");
  }

  const id = getNextSubUserId(rows);
  const row = {
    ...normalized,
    id,
    is_active: true,
    distribution: false,
    release_transfer: false,
  };

  const extras = readJson(STORAGE_EXTRAS, []);
  extras.push(row);
  writeJson(STORAGE_EXTRAS, extras);
  return row;
}

export function handleUpdateSubUser(id, payload) {
  const existing = getSubUserById(id);
  if (!existing) {
    throw new Error("Sub-user not found.");
  }

  const patch = normalizeSubUserPayload({ ...existing, ...payload });
  const rows = getSubUserRows();
  const duplicate = rows.find(row => {
    if (String(row.id) === String(id)) {
      return false;
    }
    return (
      String(row.username).toLowerCase() === patch.username.toLowerCase() ||
      String(row.email).toLowerCase() === patch.email.toLowerCase()
    );
  });
  if (duplicate) {
    throw new Error("A sub-user with this username or email already exists.");
  }

  const nextRow = { ...existing, ...patch, id: existing.id };
  const extras = readJson(STORAGE_EXTRAS, []);
  const extraIdx = extras.findIndex(row => String(row.id) === String(id));
  if (extraIdx >= 0) {
    extras[extraIdx] = { ...extras[extraIdx], ...nextRow };
    writeJson(STORAGE_EXTRAS, extras);
  } else {
    const overrides = readJson(STORAGE_OVERRIDES, {});
    overrides[String(id)] = { ...(overrides[String(id)] || {}), ...nextRow };
    writeJson(STORAGE_OVERRIDES, overrides);
  }

  return nextRow;
}

export function handlePatchSubUser(id, payload) {
  const existing = getSubUserById(id);
  if (!existing) {
    throw new Error("Sub-user not found.");
  }

  const patch = { ...(payload || {}) };
  if (Object.prototype.hasOwnProperty.call(patch, "is_active")) {
    patch.is_active = normalizeBool(patch.is_active, existing.is_active);
  }
  if (Object.prototype.hasOwnProperty.call(patch, "distribution")) {
    patch.distribution = normalizeBool(patch.distribution, existing.distribution);
  }
  if (Object.prototype.hasOwnProperty.call(patch, "release_transfer")) {
    patch.release_transfer = normalizeBool(patch.release_transfer, existing.release_transfer);
  }

  const nextRow = { ...existing, ...patch, id: existing.id };
  const seedMatch = mockSubUsersData.find(row => String(row.id) === String(id));

  if (seedMatch) {
    const overrides = readJson(STORAGE_OVERRIDES, {});
    overrides[String(id)] = { ...(overrides[String(id)] || {}), ...patch };
    writeJson(STORAGE_OVERRIDES, overrides);
  } else {
    const extras = readJson(STORAGE_EXTRAS, []);
    const extraIdx = extras.findIndex(row => String(row.id) === String(id));
    if (extraIdx >= 0) {
      extras[extraIdx] = { ...extras[extraIdx], ...patch };
      writeJson(STORAGE_EXTRAS, extras);
    }
  }

  return nextRow;
}
